<template>
  <div>
    <div>
      <v-btn id="split_secret_back_btn" icon @click="backPage">
        <v-icon>chevron_left</v-icon>
      </v-btn>

      <v-row no-gutters align="center">
        <span
          v-if="optionValue"
          class="text-h6 context_title_color--text"
        >
          {{ $tc('recover') }}
        </span>

        <span
          v-else
          class="text-h6 context_title_color--text"
        >
          {{ $tc('match') }}
        </span>

        <span class="text-h5 font-weight-medium mx-1">
          {{ '/' }}
        </span>

        <card-toolbar :title="name" class="mt-md-2" />
      </v-row>
    </div>

    <div>
      <v-stepper
        elevation="0"
        style="background: transparent;"
        vertical
        v-model="activeStep"
      >
        <template v-for="index in steps">
          <v-stepper-step
            v-if="index === (steps - 1) && showWarning && optionValue"
            :key="`${index}-step`"
            :complete="activeStep > index"
            :step="index"
          />

          <v-stepper-step
            v-else-if="index === steps && showWarning && optionValue === 0"
            :key="`${index}-step`"
            :complete="activeStep > index"
            :step="index"
          >
            {{ $tc("match_return_label") }}
          </v-stepper-step>

          <v-stepper-step
            v-else-if="index === steps && showWarning && optionValue === 1"
            :key="`${index}-step`"
            :complete="activeStep > index"
            :step="index"
          >
            {{ $tc("recover_return_label") }}
          </v-stepper-step>

          <v-stepper-step
            v-else
            :key="`${index}-step`"
            :complete="activeStep > index"
            :step="index"
          >
            <span
              v-if="index % 2 === 0"
            >
              {{ $tc("part") + " " + (index / 2) }}
            </span>
          </v-stepper-step>

          <v-stepper-content
            :key="`${index}-content`"
            :step="index"
          >
            <div v-if="index === 1">
              <v-card
                flat
                outlined
                style="background: transparent;"
                class="pa-3"
                rounded="lg"
              >
                <v-alert
                  v-if="optionValue"
                  style="background: transparent;"
                  border="left"
                  colored-border
                  type="warning"
                >
                  {{
                    $tc("recover_secret_warning")
                  }}
                </v-alert>

                <v-alert
                  v-else
                  style="background: transparent;"
                  border="left"
                  colored-border
                  type="warning"
                >
                  {{
                    $tc("match_secret_warning")
                  }}
                </v-alert>
              </v-card>

              <v-row
                no-gutters
                class="mt-4 mb-2 my-2"
                justify="end"
              >
                <v-btn
                  class="ma-2"
                  color="blue"
                  dark
                  @click="nextStep(index)"
                >
                  {{ $tc("start") }}
                </v-btn>
              </v-row>
            </div>

            <div v-else-if="index === (steps - 1) && showWarning && optionValue">
              <v-card
                flat
                outlined
                style="background: transparent;"
                class="pa-3"
                rounded="lg"
              >
                <v-alert
                  style="background: transparent;"
                  border="left"
                  colored-border
                  type="warning"
                >
                  {{
                    $tc("csp_show_warning")
                  }}
                </v-alert>
              </v-card>

              <v-row
                no-gutters
                class="mt-4 mb-2 my-2"
                justify="end"
              >
                <v-btn
                  class="ma-2"
                  color="green"
                  dark
                  @click="finish(index)"
                >
                  {{ $tc("show_csp") }}
                </v-btn>
              </v-row>
            </div>

            <div v-else-if="index === steps">
              <div v-if="loading">
                <v-card
                  flat
                  outlined
                  style="background: transparent;"
                  class="pa-3"
                  rounded="lg"
                >
                  <v-row align-content="center">
                    <v-progress-circular
                      :size="100"
                      indeterminate
                      class="mx-auto my-12"
                    />
                  </v-row>
                </v-card>
              </div>

              <div v-else>
                <v-card
                  flat
                  outlined
                  style="background: transparent;"
                  class="pa-3"
                  rounded="lg"
                >
                  <div v-if="optionValue">
                    <v-col cols="12" md="6">
                      <v-text-field
                        :id="`stepper_${index}`"
                        class="monospace"
                        :label="$tc('csp')"
                        readonly
                        :value="csp"
                        :hint="$tc('hint_csp_recover')"
                        persistent-hint
                      />

                      <span
                        class="subtitle-1 font-weight-medium"
                      >
                        {{ $tc('sec_level') }}
                      </span>

                      <p class="mb-4">
                        {{ secLevel }}
                      </p>
                    </v-col>
                  </div>

                  <div v-else>
                    <v-alert
                      v-if="matchSuccess"
                      style="background: transparent;"
                      border="left"
                      colored-border
                      type="success"
                    >
                      {{
                        $tc("match_secret_success")
                      }}
                    </v-alert>

                    <v-alert
                      v-else
                      style="background: transparent;"
                      border="left"
                      colored-border
                      type="error"
                    >
                      {{
                        $tc("match_secret_error")
                      }}
                    </v-alert>
                  </div>
                </v-card>

                <v-row
                  no-gutters
                  class="mt-4 mb-2 my-2"
                  justify="end"
                >
                  <v-btn
                    class="ma-2"
                    color="blue"
                    dark
                    @click="backPage"
                  >
                    {{ $tc("ok") }}
                  </v-btn>
                </v-row>
              </div>
            </div>

            <div v-else-if="index % 2 !== 0">
              <v-card
                flat
                outlined
                style="background: transparent;"
                class="pa-3"
                rounded="lg"
              >
                <div v-if="index === 3">
                  <v-alert
                    style="background: transparent;"
                    border="left"
                    colored-border
                    type="warning"
                  >
                    {{
                      $t("match_part1_warning")
                    }}
                  </v-alert>
                </div>

                <div v-else>
                  <v-alert
                    v-if="optionValue"
                    style="background: transparent;"
                    border="left"
                    colored-border
                    type="warning"
                  >
                    {{
                      $t("recover_part_warning")
                    }}
                  </v-alert>

                  <v-alert
                    v-else
                    style="background: transparent;"
                    border="left"
                    colored-border
                    type="warning"
                  >
                    {{
                      $t("match_part_warning")
                    }}
                  </v-alert>
                </div>
              </v-card>

              <v-row
                no-gutters
                class="mt-4 mb-2 my-2"
                justify="end"
              >
                <v-btn
                  v-if="index <= 128"
                  class="ma-2"
                  color="blue"
                  dark
                  @click="nextStep(index)"
                >
                  {{ $tc("next") }}
                </v-btn>

                <v-btn
                  v-if="index >= 4"
                  class="ma-2"
                  color="success"
                  dark
                  @click="getCSP(index)"
                >
                  {{ $tc("send") }}
                </v-btn>
              </v-row>
            </div>

            <div v-else>
              <v-card
                flat
                outlined
                style="background: transparent;"
                class="pa-3"
                rounded="lg"
              >
                <v-row align="center">
                  <v-col cols="12" md="6">
                    <v-text-field
                      :id="`stepper_${index}`"
                      class="monospace"
                      :label="$tc('shadow') + ' ' + (index / 2)"
                      v-model="shadow"
                      :error-messages="shadowErrors"
                      @input="$v.shadow.$touch()"
                      @blur="$v.shadow.$touch()"
                      counter
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      :id="`stepper_${index}`"
                      class="monospace"
                      :label="$tc('shadow_cks')"
                      v-model="cks"
                      :error-messages="cksErrors"
                      @input="$v.cks.$touch()"
                      @blur="$v.cks.$touch()"
                      counter
                    />
                  </v-col>
                </v-row>
              </v-card>

              <v-row
                no-gutters
                class="mt-4 mb-2 my-2"
                justify="end"
              >
                <v-btn
                  class="ma-2"
                  color="primary"
                  dark
                  plain
                  @click="backStep(index)"
                >
                  {{ $tc("back") }}
                </v-btn>

                <v-btn
                  class="ma-2"
                  color="blue"
                  :dark="!($v.cks.$invalid || shadow === '' || shadow === null)"
                  :disabled="$v.cks.$invalid || shadow === '' || shadow === null"
                  @click="nextStep(index)"
                >
                  {{ $tc("next") }}
                </v-btn>
              </v-row>
            </div>
          </v-stepper-content>
        </template>
      </v-stepper>
    </div>
  </div>
</template>

<script>
import CardToolbar from '@/components/CardToolbar'
import { required } from 'vuelidate/lib/validators'

export default {
  components: {
    CardToolbar
  },
  data () {
    return {
      optionValue: null,
      item: null,
      name: null,
      shareList: [],
      activeStep: 1,
      steps: 2,
      shadow: null,
      cks: null,
      secLevel: null,
      csp: null,
      matchSuccess: false,
      loading: false,
      showWarning: false,
      page: 0
    }
  },
  created () {
    this.item = this.$route.params.item
    this.name = this.$route.params.item.csp
    this.optionValue = this.$route.params.recoverValue
    this.page = this.$route.params.page
  },
  computed: {
    shadowErrors () {
      const errors = []
      if (!this.$v.shadow.$dirty) return errors
      !this.$v.shadow.required &&
        errors.push(this.$t('$validation.required'))
      return errors
    },
    cksErrors () {
      const errors = []
      if (!this.$v.cks.$dirty) return errors
      !this.$v.cks.verifyCKS &&
        errors.push(this.$t('$validation.kcv_error'))
      return errors
    }
  },
  validations () {
    const validation = {}

    const verifyCKS = async (cks) => {
      const shadow = this.shadow
      if (shadow && cks !== null) {
        const shadowCks = await this.skeepShadowCks(shadow)
        return shadowCks === cks
      } else {
        return true
      }
    }

    validation.shadow = {
      required
    }

    validation.cks = {
      verifyCKS
    }

    return validation
  },
  methods: {
    backPage () {
      const safeKeepingTab = 0

      if (this.page > 0 && this.page !== undefined) {
        this.$router.push(
          {
            name: 'partition-keys',
            query: {
              p: this.page
            }
          }
        )
      } else {
        this.$router.push(
          {
            name: 'hsm-tools',
            params: {
              tabOption: safeKeepingTab
            }
          }
        )
      }
    },
    async  skeepShadowCks (s) {
      const m = new TextEncoder().encode(s)
      const b = await crypto.subtle.digest('SHA-256', m)
      const v = new DataView(b)

      const PRIME = 9973
      const RNDI = v.getUint32(0, false)
      const CKS = RNDI % PRIME

      return CKS.toString().padStart(4, '0')
    },
    nextStep (n) {
      if (n % 2 === 0) {
        this.shareList.push(this.shadow)
        this.shadow = null
        this.cks = null
        this.$v.$reset()
      }

      this.steps = this.steps + 1
      this.activeStep = n + 1
    },
    backStep (n) {
      this.steps = this.steps - 1
      this.activeStep = n - 1
    },
    async getCSP (n) {
      if (this.optionValue) {
        this.steps = this.steps + 1
      }
      this.showWarning = true
      this.activeStep = n + 1
      await this.submit()
    },
    finish (n) {
      this.activeStep = n + 1
    },
    async submit () {
      this.loading = true
      const recover = Number(this.optionValue)

      try {
        const res = await this.$store.dispatch('hsm/skeep_match_secret', {
          body: {
            obj: this.name,
            recover,
            share: this.shareList
          }
        })

        this.secLevel = this.getSecLevel(res.sec_level)
        this.csp = res.csp
        this.matchSuccess = true
        this.$notify.success({})
      } catch (err) {
        const errObj = this.$store.getters['error/getErrorObj'](err)
        if (errObj) {
          this.$notify.hsmError(errObj, err)
        } else {
          this.$notify.error({ message: err })
        }

        if (this.optionValue) {
          this.backPage()
        }
      } finally {
        this.loading = false
      }
    },
    getSecLevel (secLevel) {
      const secLevelOptions = [
        {
          name: this.$tc('sec_level_64b'),
          value: 1
        },
        {
          name: this.$tc('sec_level_96b'),
          value: 2
        },
        {
          name: this.$tc('sec_level_112b'),
          value: 3
        },
        {
          name: this.$tc('sec_level_128b'),
          value: 4
        },
        {
          name: this.$tc('sec_level_160b'),
          value: 5
        }
      ]

      const secLevelOption = secLevelOptions.find(option => option.value === secLevel)
      return secLevelOption ? secLevelOption.name : null
    }
  }
}
</script>
